import { easeOutCubic } from "./logo_choreography"

// One-off motion for the login screen: a card arriving, a button lighting up, a sheen crossing it.
//
// Time-driven, like the wordmark (see logo_choreography): progress is read off the clock, never
// accumulated per frame, so a tab that is hidden gets no frames and simply lands wherever the motion
// should be on the first frame after it comes back -- the old Fader-on-setInterval approach left a
// background tab half-drawn for minutes.

export type Ease = (t: number) => number

export interface Tween {
    cancel(): void
}

const raf = (fn: () => void): number | undefined =>
    typeof requestAnimationFrame === "function" ? requestAnimationFrame(fn) : undefined

/**
 * Drive `apply` with eased progress 0..1 over `ms`, starting after `delay`. `apply(0)` runs at once,
 * so whatever the motion starts from (invisible, offset) is in place before the next paint. Without
 * animation frames at all (tests, a headless renderer) it lands immediately.
 */
export const tween = (
    ms: number,
    apply: (eased: number, raw: number) => void,
    { delay = 0, ease = easeOutCubic, onDone }: { delay?: number; ease?: Ease; onDone?: () => void } = {},
): Tween => {
    const startedAt = Date.now() + delay
    let cancelled = false
    let finished = false

    const finish = () => {
        if (finished) {
            return
        }
        finished = true
        apply(1, 1)
        onDone?.()
    }

    const frame = () => {
        if (cancelled || finished) {
            return
        }
        const raw = ms <= 0 ? 1 : (Date.now() - startedAt) / ms
        if (raw >= 1) {
            finish()
            return
        }
        apply(ease(Math.max(0, raw)), Math.max(0, raw))
        raf(frame)
    }

    apply(0, 0)
    if (raf(frame) === undefined) {
        finish()
    }

    return {
        cancel: () => {
            cancelled = true
        },
    }
}
